/**
 * Product Group Component
 *
 * Displays a group of catalog items for a single raw product.
 */

import { Badge } from '@/components/ui';
import { CatalogItemRow } from './CatalogItemRow';
import type { ProductGroup as ProductGroupType } from './catalog.types';
import type { PackagingOption } from '@bstock/shared';
import type { useUpdateCatalogItem, useUpdateCatalogItemPrice, useDeleteCatalogItem } from '../../hooks/useCatalog';

interface ProductGroupProps {
  group: ProductGroupType;
  supplierId: string;
  packagings: PackagingOption[];
  currency: string;
  editingItemId: string | null;
  onEditItem: (itemId: string) => void;
  onCancelEdit: () => void;
  onViewHistory: (itemId: string) => void;
  updateMutation: ReturnType<typeof useUpdateCatalogItem>;
  updatePriceMutation: ReturnType<typeof useUpdateCatalogItemPrice>;
  deleteMutation: ReturnType<typeof useDeleteCatalogItem>;
}

export function ProductGroup({
  group,
  supplierId,
  packagings,
  currency,
  editingItemId,
  onEditItem,
  onCancelEdit,
  onViewHistory,
  updateMutation,
  updatePriceMutation,
  deleteMutation,
}: ProductGroupProps) {
  return (
    <div className="bg-neutral-50 rounded-lg p-4">
      {/* Product Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-medium text-neutral-900">{group.rawProduct.name}</h3>
          <p className="text-xs text-neutral-500">
            SKU: {group.rawProduct.sku} | Unite: {group.rawProduct.recipeUnit.symbol}
          </p>
        </div>
        <Badge variant="neutral">{group.items.length} conditionnement(s)</Badge>
      </div>

      {/* Packaging Items */}
      <div className="space-y-2">
        {group.items.map((item) => (
          <CatalogItemRow
            key={item.id}
            item={item}
            supplierId={supplierId}
            recipeUnitSymbol={group.rawProduct.recipeUnit.symbol}
            packagings={packagings}
            currency={currency}
            isEditing={editingItemId === item.id}
            onEdit={() => onEditItem(item.id)}
            onCancelEdit={onCancelEdit}
            onViewHistory={() => onViewHistory(item.id)}
            updateMutation={updateMutation}
            updatePriceMutation={updatePriceMutation}
            deleteMutation={deleteMutation}
          />
        ))}
      </div>
    </div>
  );
}
